import React, { useEffect, useMemo, useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view-pro';
import dayjs from 'dayjs';
import { Box, Typography, Stack, IconButton, Badge, Popover } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PeopleIcon from '@mui/icons-material/People';
import useUsers from '../../context/UsersContext';
import { CheckboxUI, StaticDatePickerUI } from '../Interface';
import CalendarFilterItem from './CalendarFilterItem';

const USER_FIELD = 'responsiblePersonId';
const EMPLOYEE_FIELD = 'employeeId';

const updateFilterItem = (items, field, value, shouldAdd) => {
  const currentItem = items.find((item) => item.field === field);
  const currentValues = Array.isArray(currentItem?.value) ? currentItem.value : [];

  const updatedValues = shouldAdd
    ? currentValues.map(String).includes(String(value))
      ? currentValues
      : [...currentValues, value]
    : currentValues.filter((v) => String(v) !== String(value));

  const newItems = items.filter((item) => item.field !== field);

  if (updatedValues.length > 0) {
    newItems.push({
      field,
      operator: 'isAnyOf',
      value: updatedValues,
    });
  }

  return newItems;
};

const CalendarFilter = ({ dates, resources, queryState, hasResourceFilter, roles, isLgUp, isLoading }) => {
  const { user } = useUsers();
  const { date, setDate } = dates;
  const { query, setQuery } = queryState;

  const [filterItemAnchorEl, setFilterItemAnchorEl] = useState(null);
  const [personFilterAnchorEl, setPersonFilterAnchorEl] = useState(null);

  const items = query.filter?.filterModel?.items ?? [];

  useEffect(() => {
    if (!user?.userId || isLoading) return;

    const currentUser = resources?.user?.find((resourceUser) => resourceUser.userId === user.userId);

    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];

      let newItems = [...prevItems];

      if (roles.TASK_CALENDAR_SEE_ONLY_OWN) {
        newItems = newItems.filter((item) => item.field !== USER_FIELD && item.field !== EMPLOYEE_FIELD);

        newItems.push({
          field: USER_FIELD,
          operator: 'isAnyOf',
          value: [user.userId],
        });

        if (currentUser?.employeeId) {
          newItems.push({
            field: EMPLOYEE_FIELD,
            operator: 'isAnyOf',
            value: [currentUser.employeeId],
          });
        }
      } else {
        const hasUserFilter = newItems.some((item) => item.field === USER_FIELD);

        if (!hasUserFilter) {
          newItems.push({
            field: USER_FIELD,
            operator: 'isAnyOf',
            value: [user.userId],
          });
        }

        const hasEmployeeFilter = newItems.some((item) => item.field === EMPLOYEE_FIELD);

        if (currentUser?.employeeId && !hasEmployeeFilter) {
          newItems.push({
            field: EMPLOYEE_FIELD,
            operator: 'isAnyOf',
            value: [currentUser.employeeId],
          });
        }
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  }, [roles.TASK_CALENDAR_SEE_ONLY_OWN, user?.userId, resources?.user, isLoading]);

  const getFieldItem = (field) => items.find((item) => item.field === field);

  const handleFilterChange = (field, value, operator = 'is') => {
    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];
      const newItems = prevItems.filter((i) => i.field !== field);

      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        newItems.push({
          field,
          operator,
          value,
        });
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  };

  const isChecked = (field, id) => {
    const item = getFieldItem(field);

    if (!item || !Array.isArray(item.value)) return false;
    return item.value.map(String).includes(String(id));
  };

  const handleToggle = (field, id) => {
    if (roles.TASK_CALENDAR_SEE_ONLY_OWN) return;

    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];

      const currentItem = prevItems.find((item) => item.field === field);
      const currentValues = Array.isArray(currentItem?.value) ? currentItem.value : [];

      const shouldAdd = !currentValues.map(String).includes(String(id));

      let newItems = updateFilterItem(prevItems, field, id, shouldAdd);

      if (field === USER_FIELD) {
        const selectedUser = resources?.user?.find((resourceUser) => String(resourceUser.userId) === String(id));

        if (selectedUser?.employeeId) {
          newItems = updateFilterItem(newItems, EMPLOYEE_FIELD, selectedUser.employeeId, shouldAdd);
        }
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  };

  const renderCheckLabel = (field, id, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
      <CheckboxUI
        sx={{ m: 0, p: 0 }}
        checked={isChecked(field, id)}
        onChange={() => handleToggle(field, id)}
        disabled={roles.TASK_CALENDAR_SEE_ONLY_OWN}
      />
      <Typography>{label}</Typography>
    </Box>
  );

  const personFilterCount = useMemo(() => {
    const responsiblePersonItem = items.find((item) => item.field === USER_FIELD)?.value ?? [];

    return responsiblePersonItem.length;
  }, [items]);

  const otherFilterCount = useMemo(() => {
    return items.filter(({ field }) => field !== USER_FIELD && field !== EMPLOYEE_FIELD).length;
  }, [items]);

  const commonFilterItemProps = {
    hasResourceFilter,
    getFieldItem,
    handleFilterChange,
  };

  return (
    <Box>
      {isLgUp ? (
        <>
          <StaticDatePickerUI
            sx={{
              '& .MuiDatePickerToolbar-root': { display: 'none' },
              '& .MuiDialogActions-root': { display: 'none' },
              ' & .MuiDateCalendar-root': { background: '#f3f4f6' },
            }}
            value={dayjs(date)}
            onChange={(newDate) => {
              setDate((prevState) => ({
                ...prevState,
                startDate: newDate ? newDate.startOf('day') : null,
                endDate: newDate ? newDate.endOf('day') : null,
              }));
            }}
          />
          <Box ml={1} mb={1}>
            <CalendarFilterItem {...commonFilterItemProps} />
          </Box>
          <Box>
            <SimpleTreeView>
              <TreeItem itemId="responsibles-root" label="Felelősök">
                {resources?.user?.map((user) => (
                  <TreeItem
                    key={`resp-${user.userId}`}
                    itemId={`resp-${user.userId}`}
                    label={renderCheckLabel(USER_FIELD, user.userId, user.nickName)}
                  />
                ))}
              </TreeItem>
            </SimpleTreeView>

            {resources?.employee?.length > 0 && (
              <SimpleTreeView>
                <TreeItem itemId="employees-root" label="Dolgozók">
                  {resources?.employee?.map((employee) => (
                    <TreeItem
                      key={`emp-${employee.id}`}
                      itemId={`emp-${employee.id}`}
                      label={renderCheckLabel(EMPLOYEE_FIELD, employee.id, employee.name)}
                    />
                  ))}
                </TreeItem>
              </SimpleTreeView>
            )}
          </Box>
        </>
      ) : (
        <Stack direction="row" spacing={2} m={1}>
          <Box>
            <IconButton
              disableRipple
              onClick={(e) => setFilterItemAnchorEl(e.currentTarget)}
              sx={{ backgroundColor: '#78b8ef', color: '#fff', borderRadius: 2, p: 0.4 }}
            >
              <Badge badgeContent={otherFilterCount} color="secondary">
                <FilterAltIcon fontSize="large" />
              </Badge>
            </IconButton>
          </Box>
          <Box>
            <IconButton
              disableRipple
              onClick={(e) => setPersonFilterAnchorEl(e.currentTarget)}
              sx={{ backgroundColor: '#78b8ef', color: '#fff', borderRadius: 2, p: 0.4 }}
            >
              <Badge badgeContent={personFilterCount} color="secondary">
                <PeopleIcon fontSize="large" />
              </Badge>
            </IconButton>
          </Box>
        </Stack>
      )}

      <Popover
        id="calendar-filter-item-popover"
        open={Boolean(filterItemAnchorEl)}
        anchorEl={filterItemAnchorEl}
        onClose={() => setFilterItemAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              p: 2,
            },
          },
        }}
      >
        <CalendarFilterItem {...commonFilterItemProps} />
      </Popover>

      <Popover
        id="calendar-person-filter-popover"
        open={Boolean(personFilterAnchorEl)}
        anchorEl={personFilterAnchorEl}
        onClose={() => setPersonFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 360,
              p: 2,
            },
          },
        }}
      >
        {resources?.user?.map((user) => (
          <Box key={user.userId} mb={0.5}>
            {renderCheckLabel(USER_FIELD, user.userId, user.nickName)}
          </Box>
        ))}
      </Popover>
    </Box>
  );
};

export default CalendarFilter;
